import React, { useState } from 'react'
import { AlertTriangle, CheckCircle2, ChevronDown, ChevronUp, ListTodo, Sparkles, Target, X } from 'lucide-react'
import { briefingInlineSegments, briefingItems } from '../../lib/resumeBriefing'
import type { ResumeBriefingResponse } from '../../lib/resumeBriefing'

interface ResumeBriefingProps {
  briefing: ResumeBriefingResponse
  onDismiss: () => void
}

function BriefingInlineText({ value }: { value: string }) {
  return <>{briefingInlineSegments(value).map((segment, index) => (
    segment.bold
      ? <strong key={`${index}-${segment.text}`}>{segment.text}</strong>
      : <React.Fragment key={`${index}-${segment.text}`}>{segment.text}</React.Fragment>
  ))}</>
}

function BriefingSection({ title, value, icon }: { title: string; value: string; icon: React.ReactNode }) {
  const items = briefingItems(value)
  if (!items.length) return null
  return (
    <section className="resume-briefing-section">
      <h3>{icon}{title}</h3>
      {items.length === 1
        ? <p><BriefingInlineText value={items[0]} /></p>
        : <ul>{items.map(item => <li key={item}><BriefingInlineText value={item} /></li>)}</ul>}
    </section>
  )
}

function formatSummaryTime(value: string | null) {
  if (!value) return 'Durable conversation summary'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return 'Durable conversation summary'
  return `Summary updated ${new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }).format(date)}`
}

export default function ResumeBriefing({ briefing, onDismiss }: ResumeBriefingProps) {
  const [expanded, setExpanded] = useState(false)
  const { sections } = briefing

  return (
    <aside className="resume-briefing" aria-labelledby="resume-briefing-title">
      <div className="resume-briefing-header">
        <div className="resume-briefing-heading">
          <span className="resume-briefing-icon"><Sparkles size={14} /></span>
          <div>
            <h2 id="resume-briefing-title">Resume briefing</h2>
            <p>{formatSummaryTime(briefing.updated_at)} · Verify inferred details before acting.</p>
          </div>
        </div>
        <button type="button" onClick={onDismiss} aria-label="Dismiss resume briefing" title="Dismiss for this session"><X size={15} /></button>
      </div>

      <div className="resume-briefing-grid">
        <BriefingSection title="Objective" value={sections.objective} icon={<Target size={13} />} />
        <BriefingSection title="Confirmed" value={sections.confirmed_decisions} icon={<CheckCircle2 size={13} />} />
        <BriefingSection title="Next steps" value={sections.open_questions} icon={<ListTodo size={13} />} />
      </div>

      <button
        type="button"
        className="resume-briefing-expand"
        onClick={() => setExpanded(value => !value)}
        aria-expanded={expanded}
      >
        {expanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
        {expanded ? 'Hide current state and completed work' : 'Show current state and completed work'}
      </button>

      {expanded && (
        <div className="resume-briefing-grid resume-briefing-details">
          <BriefingSection title="Current state" value={sections.current_state} icon={<CheckCircle2 size={13} />} />
          <BriefingSection title="Completed" value={sections.completed_work} icon={<CheckCircle2 size={13} />} />
          <BriefingSection title="Cautions" value={sections.cautions} icon={<AlertTriangle size={13} />} />
        </div>
      )}
    </aside>
  )
}
